import React, { Fragment } from "react";

import type { Row, RowData } from "@tanstack/react-table-v9";

import BodyCell from "./BodyCell";
import { inColumnWindow } from "./columnWindow";
import { columnLayout, orderedCells } from "./layout";
import Button from "../../../Button/Button";
import ErrorMessage from "../../../ErrorMessage/ErrorMessage";
import SkeletonLoader from "../../../SkeletonLoader/SkeletonLoader";
import { EXPANDER_COLUMN_ID, SELECTION_COLUMN_ID } from "../../columns";
import type { TableV2Features } from "../../features";
import { getColumnAlign } from "../../formatters";
import type {
  RowVirtualizer,
  VirtualWindow,
} from "../../hooks/useGridVirtualizer";
import type { TableV2Instance } from "../../hooks/useTableV2";
import { BodyRow, CellBox, ExpandedPanel, StateMessage } from "../../styles";

const TOGGLE_COLUMNS = new Set([SELECTION_COLUMN_ID, EXPANDER_COLUMN_ID]);

/** Rows, or the error, loading and empty state in their place */
const GridBody = <TData extends RowData>({
  table,
  isLoading,
  error,
  onRetry,
  emptyMessage,
  skeletonCount,
  rowHeight,
  renderSubComponent,
  rowEmphasis,
  virtualRows,
  rowWindow,
  columnWindow,
}: {
  table: TableV2Instance<TData>;
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage: string;
  skeletonCount: number;
  rowHeight: number;
  renderSubComponent?: (row: Row<TableV2Features, TData>) => React.ReactNode;
  rowEmphasis?: (row: Row<TableV2Features, TData>) => boolean;
  virtualRows: boolean;
  rowWindow: RowVirtualizer | null;
  columnWindow: VirtualWindow | null;
}) => {
  const fixedWidth = !!columnWindow;

  if (error) {
    return (
      <StateMessage role="alert">
        <ErrorMessage>{error}</ErrorMessage>
        {onRetry && (
          <Button small variant="secondary" text="Retry" onClick={onRetry} />
        )}
      </StateMessage>
    );
  }

  if (isLoading) {
    const leafColumns = table.getVisibleLeafColumns();
    return (
      <>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <BodyRow
            key={`skeleton-${i}`}
            role="row"
            data-testid="skeleton-row"
            $rowHeight={rowHeight}
          >
            {inColumnWindow(
              leafColumns,
              (column) => column,
              columnWindow,
              (column) => (
                <CellBox
                  key={column.id}
                  role="cell"
                  $align={getColumnAlign(column.columnDef.meta)}
                  $pinned={column.getIsPinned()}
                  $overflow={
                    TOGGLE_COLUMNS.has(column.id) ||
                    column.columnDef.meta?.overflow
                  }
                  style={columnLayout(
                    column,
                    column.getSize(),
                    false,
                    fixedWidth,
                  )}
                >
                  {TOGGLE_COLUMNS.has(column.id) ? (
                    <SkeletonLoader
                      height={24}
                      width={24}
                      style={{ flexShrink: 0 }}
                    />
                  ) : (
                    <SkeletonLoader height={24} width="80%" />
                  )}
                </CellBox>
              ),
            )}
          </BodyRow>
        ))}
      </>
    );
  }

  const rows = table.getRowModel().rows;
  if (rows.length === 0) return <StateMessage>{emptyMessage}</StateMessage>;

  const renderRow = (row: Row<TableV2Features, TData>) => {
    const expanded = row.getIsExpanded();
    return (
      <Fragment key={row.id}>
        <BodyRow
          role="row"
          $isExpanded={expanded && !!renderSubComponent}
          $emphasis={rowEmphasis?.(row)}
          $rowHeight={rowHeight}
        >
          {inColumnWindow(
            orderedCells(row),
            (cell) => cell.column,
            columnWindow,
            (cell) => (
              <BodyCell
                key={cell.id}
                table={table}
                cell={cell}
                fixedWidth={fixedWidth}
              />
            ),
          )}
        </BodyRow>
        {expanded && renderSubComponent && (
          <ExpandedPanel>{renderSubComponent(row)}</ExpandedPanel>
        )}
      </Fragment>
    );
  };

  if (!virtualRows) return <>{rows.map(renderRow)}</>;
  // Until the scroll container is found, show the first 50 rows
  if (!rowWindow) return <>{rows.slice(0, 50).map(renderRow)}</>;
  return (
    <>
      <div style={{ height: rowWindow.before }} />
      {rowWindow.indexes.map((index) => (
        <div
          key={rows[index].id}
          data-index={index}
          ref={rowWindow.measureElement}
        >
          {renderRow(rows[index])}
        </div>
      ))}
      <div style={{ height: rowWindow.after }} />
    </>
  );
};

export default GridBody;
